'use client';

/* eslint-disable import/no-unassigned-import */
import { c as _c } from "react/compiler-runtime";
import '@vidstack/react/player/styles/default/theme.css';
import '@vidstack/react/player/styles/default/layouts/audio.css';
import '@vidstack/react/player/styles/default/layouts/video.css';
/* eslint-enable import/no-unassigned-import */
import { MediaPlayer, type MediaPlayerInstance, MediaProvider, type PlayerSrc, Poster } from '@vidstack/react';
import { DefaultAudioLayout, defaultLayoutIcons, DefaultVideoLayout } from '@vidstack/react/player/layouts/default';
import classNames from 'classnames';
import Image, { type StaticImageData } from 'next/image';
import { usePathname, useRouter } from 'next/navigation';
import { type JSX, type ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { FocusScope } from 'react-aria';
import { createPortal } from 'react-dom';
import Skeleton from 'react-loading-skeleton';
import { GTM_ACTION_CLICK, GTM_EVENT_NAME_INTERNAL_LINK_CLICK, GTM_EVENT_PAGE_VIEW, GTM_EVENT_USER_INTERACTIONS } from '@shared/utils/constants/gtm';
import { sendGtmEvent } from '@shared/utils/helpers/send-gtm-event';
import { useIsIdle } from '@shared/utils/hooks/use-is-idle';
import { useMounted } from '@shared/utils/hooks/use-mounted';
import { type RecommendationCardProps } from './components/recommedation-card/recommendation-card';
import { SEEK_STEP, SeekButtonWithTooltip } from './components/seek-button-with-tooltip/seek-button-with-tooltip';
import { type PlayerDefaultTranslations } from './types/player-default-translations';
import styles from './player.module.scss';
type PlayerProps = {
  cmsContent: PlayerDefaultTranslations;
  displayControls?: boolean;
  recommendation?: {
    authors: string[];
    contentTypeIcon?: ReactNode;
    isExternal: boolean;
    thumbnail: {
      altText?: string;
      fallbackSrc?: StaticImageData | string;
      src?: string;
    };
    title: string;
    url: string;
  };
  RecommendationCard?: (props: Omit<RecommendationCardProps, 'LinkComponent'>) => JSX.Element;
  recommendationContainerId?: string;
  src: string;
  srcType: string;
  videoPoster?: {
    altText?: string;
    src?: string;
  };
};
const NEXT_RECOMMENDATION_URL_KEY = 'nextRecommendationUrlKey';
const NEXT_RECOMMENDATION_REDIRECTS_WHEN_IDLE = 'nextRecommendationRedirectsWhenIdle';
const NEXT_RECOMMENDATION_REDIRECT_COUNT_BEFORE_DISABLE = 5;
export const Player = t0 => {
  const $ = _c(35);
  const {
    cmsContent,
    displayControls: t1,
    recommendation,
    RecommendationCard,
    recommendationContainerId,
    src,
    srcType,
    videoPoster
  } = t0;
  const displayControls = t1 === undefined ? true : t1;
  const isMounted = useMounted();
  const router = useRouter();
  const pathname = usePathname();
  const isIdle = useIsIdle();
  const [isRecommendationCardVisible, setIsRecommendationCardVisible] = useState(false);
  const [isStarted, setIsStarted] = useState(false);
  const isBrowser = typeof window !== "undefined";
  let t2;
  bb0: {
    if (isBrowser && isIdle) {
      const repeatedInIdle = sessionStorage.getItem(NEXT_RECOMMENDATION_REDIRECTS_WHEN_IDLE);
      if (repeatedInIdle !== null && Number(repeatedInIdle) >= NEXT_RECOMMENDATION_REDIRECT_COUNT_BEFORE_DISABLE) {
        t2 = false;
        break bb0;
      }
    }
    t2 = true;
  }
  const nextRecommendationEnabled = t2;
  const cancelButtonRef = useRef(null);
  const videoPlayerRef = useRef(null);
  let t3;
  if ($[0] !== cmsContent.autoPlayLabel || $[1] !== cmsContent.cancelLabel || $[2] !== cmsContent.playNowLabel || $[3] !== cmsContent.recommendationCardDescription || $[4] !== cmsContent.recommendationCardDescriptionSecondary) {
    t3 = {
      autoPlayLabel: cmsContent.autoPlayLabel,
      cancelLabel: cmsContent.cancelLabel,
      playNowLabel: cmsContent.playNowLabel,
      recommendationCardDescription: cmsContent.recommendationCardDescription,
      recommendationCardDescriptionSecondary: cmsContent.recommendationCardDescriptionSecondary
    };
    $[0] = cmsContent.autoPlayLabel;
    $[1] = cmsContent.cancelLabel;
    $[2] = cmsContent.playNowLabel;
    $[3] = cmsContent.recommendationCardDescription;
    $[4] = cmsContent.recommendationCardDescriptionSecondary;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const recommendationCardCmsContent = t3;
  let t4;
  if ($[6] !== isMounted) {
    t4 = [isMounted];
    $[6] = isMounted;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  useEffect(_temp3, t4);
  let t5;
  if ($[8] !== RecommendationCard || $[9] !== isIdle || $[10] !== nextRecommendationEnabled || $[11] !== recommendation || $[12] !== recommendationCardCmsContent || $[13] !== router) {
    t5 = nextRecommendationEnabled && recommendation && RecommendationCard && <div className={styles["container__recommendation-card-container"]}><FocusScope contain={true}><div aria-labelledby="autoplay-cancel-button" aria-modal="true" role="dialog"><RecommendationCard cancelButtonRef={cancelButtonRef} cmsContent={recommendationCardCmsContent} {...recommendation} onClose={() => {
            setIsRecommendationCardVisible(false);
            sendGtmEvent({
              action: GTM_ACTION_CLICK,
              event: GTM_EVENT_USER_INTERACTIONS,
              eventName: GTM_EVENT_NAME_INTERNAL_LINK_CLICK,
              label: "watch_next_close"
            });
          }} onCountdownEnd={() => {
            if (isBrowser) {
              sessionStorage.setItem(NEXT_RECOMMENDATION_URL_KEY, recommendation.url);
              if (isIdle) {
                const newRepeatedOnIdle = Number(sessionStorage.getItem(NEXT_RECOMMENDATION_REDIRECTS_WHEN_IDLE) ?? "0") + 1;
                sessionStorage.setItem(NEXT_RECOMMENDATION_REDIRECTS_WHEN_IDLE, newRepeatedOnIdle.toString());
              } else {
                sessionStorage.removeItem(NEXT_RECOMMENDATION_REDIRECTS_WHEN_IDLE);
              }
            }
            sendGtmEvent({
              event: GTM_EVENT_USER_INTERACTIONS,
              eventName: GTM_EVENT_PAGE_VIEW,
              label: "watch_next_autorefresh"
            });
            if (isBrowser && recommendation.isExternal) {
              window.open(recommendation.url, "_blank", "noopener,noreferrer");
            } else {
              router.push(recommendation.url);
            }
          }} /></div></FocusScope></div>;
    $[8] = RecommendationCard;
    $[9] = isIdle;
    $[10] = nextRecommendationEnabled;
    $[11] = recommendation;
    $[12] = recommendationCardCmsContent;
    $[13] = router;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  const recommendationOverlay = t5;
  const t6 = !displayControls || isRecommendationCardVisible;
  let t7;
  if ($[15] !== t6) {
    t7 = classNames(styles.container, {
      [styles["container--controls-hidden"]]: t6
    });
    $[15] = t6;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  let t8;
  if ($[17] !== RecommendationCard || $[18] !== cmsContent || $[19] !== isMounted || $[20] !== isRecommendationCardVisible || $[21] !== isStarted || $[22] !== nextRecommendationEnabled || $[23] !== pathname || $[24] !== recommendation || $[25] !== recommendationContainerId || $[26] !== recommendationOverlay || $[27] !== src || $[28] !== srcType || $[29] !== videoPoster?.altText || $[30] !== videoPoster?.src) {
    t8 = isMounted && localStorage ? <MediaPlayer autoPlay={isBrowser && sessionStorage.getItem(NEXT_RECOMMENDATION_URL_KEY) === pathname} crossOrigin={true} onEnd={() => {
      if (!recommendation || !nextRecommendationEnabled) {
        return;
      }
      if (!isRecommendationCardVisible) {
        videoPlayerRef?.current?.el?.focus();
      }
      if (isRecommendationCardVisible && cancelButtonRef.current) {
        cancelButtonRef.current.focus();
      }
      setIsRecommendationCardVisible(true);
    }} onPlay={() => {
      setIsStarted(true);
    }} onStarted={() => {
      if (isBrowser) {
        sessionStorage.removeItem(NEXT_RECOMMENDATION_URL_KEY);
      }
    }} playsInline={true} ref={videoPlayerRef} src={{
      src,
      type: srcType
    } as PlayerSrc}>{recommendation && RecommendationCard && isRecommendationCardVisible && (recommendationContainerId ? createPortal(recommendationOverlay, document.querySelector(`#${recommendationContainerId}`)) : recommendationOverlay)}<DefaultAudioLayout colorScheme="light" icons={defaultLayoutIcons} translations={cmsContent} /><DefaultVideoLayout colorScheme="light" icons={defaultLayoutIcons} seekStep={SEEK_STEP} slots={{
        afterPlayButton: <SeekButtonWithTooltip label={cmsContent["Seek Forward"]} type="forward" />,
        beforePlayButton: <SeekButtonWithTooltip label={cmsContent["Seek Backward"]} type="backward" />,
        downloadButton: null,
        pipButton: null
      }} translations={cmsContent} /><MediaProvider>{videoPoster?.src && <Poster asChild={true} className={classNames(styles.container__poster, {
          [styles["container__poster--hidden"]]: isStarted
        })}><Image alt={videoPoster?.altText ?? ""} fill={true} src={videoPoster?.src} /></Poster>}</MediaProvider></MediaPlayer> : <Skeleton className={styles.container__skeleton} />;
    $[17] = RecommendationCard;
    $[18] = cmsContent;
    $[19] = isMounted;
    $[20] = isRecommendationCardVisible;
    $[21] = isStarted;
    $[22] = nextRecommendationEnabled;
    $[23] = pathname;
    $[24] = recommendation;
    $[25] = recommendationContainerId;
    $[26] = recommendationOverlay;
    $[27] = src;
    $[28] = srcType;
    $[29] = videoPoster?.altText;
    $[30] = videoPoster?.src;
    $[31] = t8;
  } else {
    t8 = $[31];
  }
  let t9;
  if ($[32] !== t7 || $[33] !== t8) {
    t9 = <div className={t7}>{t8}</div>;
    $[32] = t7;
    $[33] = t8;
    $[34] = t9;
  } else {
    t9 = $[34];
  }
  return t9;
};
function _temp(event) {
  event.preventDefault();
}
function _temp2(event_0) {
  event_0.preventDefault();
}
function _temp3() {
  const videoElement = document.querySelector("video");
  const disableContextMenu = _temp;
  const disableLongPress = _temp2;
  if (videoElement) {
    videoElement.setAttribute("controlsList", "nodownload");
    videoElement.addEventListener("contextmenu", disableContextMenu);
    videoElement.addEventListener("touchstart", disableLongPress);
  }
  return () => {
    if (videoElement) {
      videoElement.removeEventListener("contextmenu", disableContextMenu);
      videoElement.removeEventListener("touchstart", disableLongPress);
    }
  };
}